'use client';

import { useTranslations, useLocale } from "next-intl";
import Image from "next/image";
import { useState } from "react";
import { MdArrowBackIos } from "react-icons/md";
import useFetchPosts from "@/components/post/fetcher";
import { HoverEffect } from "@/components/post/card-hover-effect";
import ScrollGuideButton from "@/components/home-page/scrollDown";

export default function Products() {
  const t = useTranslations('Blog');
  const l = useLocale();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 28;

  const { posts, error, isLoading } = useFetchPosts();

  const totalPages = Math.ceil(posts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = posts.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => setCurrentPage(page);
  const handleNext = () => currentPage < totalPages && setCurrentPage(currentPage + 1);
  const handlePrevious = () => currentPage > 1 && setCurrentPage(currentPage - 1);

  const getLocalizedField = (item: any, field: string) => item[`${field}_${l}`] || item[`${field}_en`];

  // Map paginated posts into the shape HoverEffect expects
  const projects = currentItems.map((item: any) => {
    // ✅ Fix: Get the image directly from the item
    const image = item.image;
    return {
      title: getLocalizedField(item, "title"),
      description: getLocalizedField(item, "description"),
      link: `/${l}/blog/${getLocalizedField(item, "url")}`,
      image: image ? `${process.env.NEXT_PUBLIC_IMAGE}/${image}` : "", 
    };
  });

  return (
    <div className="bg-yel">
      {/* Hero Section */}
            <div className="h-48 sm:h-60 sm:col-span-1 relative flex items-center m-1 lg:m-3 rounded-xl bg-secondary">
              <Image
                src="/h.avif"
                alt={t('tag')}
                fill
                sizes="(max-width: 768px) 100vw, 50vw"
                style={{ objectFit: "cover" }}
                className="z-0 rounded-xl"
                priority
              />
              <div className="absolute inset-0 bg-black bg-opacity-60 rounded-xl"></div>
              <div className="absolute sm:bottom-10 mx-5 lg:mx-10 px-1 shadow-lg">
                <h1 className="text-xl md:text-2xl font-bold uppercase text-yel mb-3 xl:mb-8">
                {t('Latest News')}
                </h1>
                <p className="max-w-4xl text-yel text-sm lg:text-lg">
                {t('description')}
                </p>
              </div>
            </div>
      <ScrollGuideButton />

      {/* Card Grid */}
      <div className="lg:px-8 px-3">
        {isLoading || error ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-10">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-60 lg:h-96 bg-yel animate-pulse rounded-2xl" />
            ))}
          </div>
        ) : (
          <HoverEffect items={projects} />
        )}
      </div>

      {/* Pagination */}
      {posts.length > itemsPerPage && (
        <div className="w-full px-3 sm:px-10 lg:px-20">
          <div className="flex justify-between items-center gap-2 pb-20 mt-10 flex-wrap">
            <button
              disabled={currentPage === 1}
              onClick={handlePrevious}
              className="text-b hover:text-gold flex items-center"
            >
              <MdArrowBackIos className={l === 'ar' ? "rotate-180" : ""} />
              <p className="capitalize font-semibold">{t('previous')}</p>
            </button>

            <div className="flex gap-2 flex-wrap">
              {Array.from({ length: totalPages }, (_, index) => (
                <button
                  key={index + 1}
                  onClick={() => handlePageChange(index + 1)}
                  className={`px-2 rounded-2xl font-bold opacity-70 border border-b ${
                    currentPage === index + 1
                      ? "bg-gold text-secondary"
                      : "bg-secondary text-gold hover:bg-b hover:text-yel"
                  }`}
                >
                  {index + 1}
                </button>
              ))}
            </div>

            <button
              disabled={currentPage === totalPages}
              onClick={handleNext}
              className="text-b hover:text-gold flex items-center"
            >
              <p className="capitalize font-semibold">{t('next')}</p>
              <MdArrowBackIos className={l === 'ar' ? "" : "rotate-180"} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}